/**
 * Preload Service
 * Dynamically preload critical assets for better performance
 */

import { createLogger } from '../core/logger';
import { FunnelConfig } from '../types';

const logger = createLogger('Preload');

/**
 * Preload critical funnel assets
 * Called after funnel JSON is loaded but before render
 */
export function preloadFunnelAssets(funnel: FunnelConfig): void {
  const preloadPromises: Promise<void>[] = [];

  // Preload first question's image (it's above the fold)
  const firstQuestionImage = funnel.questions[0]?.image;
  if (firstQuestionImage) {
    preloadPromises.push(preloadImage(firstQuestionImage));
  }

  // Preload logo if present
  if (funnel.customLogo) {
    preloadPromises.push(preloadImage(funnel.customLogo));
  }

  // Preload second question's image (likely to be seen quickly)
  const secondQuestionImage = funnel.questions[1]?.image;
  if (secondQuestionImage) {
    preloadPromises.push(preloadImage(secondQuestionImage));
  }

  if (preloadPromises.length > 0) {
    logger.info('Preloading critical assets', { count: preloadPromises.length });
    Promise.all(preloadPromises).then(() => {
      logger.debug('Asset preloading complete');
    });
  }
}

/**
 * Preload a single image
 */
function preloadImage(src: string): Promise<void> {
  return new Promise((resolve) => {
    // Use link preload for better priority
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    link.onload = () => {
      logger.debug('Image preloaded', { src: src.substring(0, 50) + '...' });
      resolve();
    };
    link.onerror = () => {
      logger.warn('Failed to preload image', { src });
      resolve(); // Don't block on failure
    };
    document.head.appendChild(link);
  });
}

/**
 * Preconnect to a domain for faster subsequent requests
 */
export function preconnectToDomain(domain: string): void {
  // Check if already preconnected
  const existing = document.querySelector(`link[rel="preconnect"][href="${domain}"]`);
  if (existing) return;

  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = domain;
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);

  logger.debug('Added preconnect', { domain });
}

/**
 * Preload next question's assets when user is likely to proceed
 */
export function preloadNextQuestion(funnel: FunnelConfig, currentIndex: number): void {
  const nextQuestion = funnel.questions[currentIndex + 1];
  if (!nextQuestion) return;

  if (nextQuestion.image) {
    preloadImage(nextQuestion.image);
  }
  if (nextQuestion.video) {
    // For video, just preconnect - don't download entire video
    const videoUrl = new URL(nextQuestion.video, window.location.href);
    preconnectToDomain(videoUrl.origin);
  }
}
